import { useEffect, useRef, useState, useCallback, type ChangeEvent } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  TablePagination,
  Alert,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import type { Screenshot, ScreenshotFilter } from '@/types';
import { FormattedTime } from './ui/formatted-time';
import { colors } from '@/theme/tokens';

const PAGE_SIZE = 8;

// Thumbnail that handles two orthogonal problems:
//   1. Race between the DB row being created and the device finishing its
//      GCS upload — show "Uploading…" and track the latest signed URL from
//      props so we retry against the freshest signature each poll cycle.
//   2. Signed URLs change on every list refetch (new signature each call)
//      which would otherwise cause the browser to re-download the image
//      every few seconds. Once the image has loaded successfully we LATCH
//      that URL and ignore subsequent prop changes for the same mounted
//      row. Before first success we always follow the latest src so a
//      stuck initial URL can't block recovery.
// Time window during which we keep retrying a missing image. Past this
// we assume the device never uploaded (lost network mid-capture, crashed
// before flush, etc.) and show "Image unavailable" instead of an
// infinite spinner. Chosen to comfortably exceed the normal
// capture → upload round-trip.
const STALE_AFTER_MS = 10 * 60 * 1000;

function ScreenshotThumb({
  src,
  alt,
  height,
  capturedAt,
  failed: forceFailed,
  onLoaded,
}: {
  src: string | undefined;
  alt: string;
  height: number | string;
  capturedAt?: string;
  // When true the parent already knows the capture failed (backend set
  // file_size = -1 after the device declined or timed out). We skip
  // straight to the "unavailable" state instead of fetching/retrying.
  failed?: boolean;
  // Fired the first time the underlying <img> reports onLoad. Lets the
  // parent treat a successful thumb as "viewer-ready" even when the row
  // is still file_size = 0 (HEAD verification pending) — the file is
  // clearly there if the browser just rendered it.
  onLoaded?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // The URL currently handed to <img>. We DO NOT just bind to the `src`
  // prop because parent polling rotates signed URLs every few seconds —
  // if we swap src mid-load the browser cancels the in-flight request
  // and starts a new one, potentially forever on slow networks or big
  // PNGs (we saw thumbs that opened fine in the viewer but never
  // triggered onLoad on the gallery tile).
  //
  // Lifecycle:
  //   - initialise from the first non-empty `src`
  //   - leave it alone while a fetch is in progress
  //   - on error, retry with a cache-bust; after `maxAttempts`, adopt
  //     the parent's latest `src` (which may carry a fresher signature
  //     or just be a different object that has since landed)
  //   - on success, freeze it permanently so poll rotations don't cause
  //     re-downloads
  const [currentSrc, setCurrentSrc] = useState<string | undefined>(src);
  const [bust, setBust] = useState(0);
  const attemptsRef = useRef(0);
  const maxAttempts = 3;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryDelayMs = 3000;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Seed currentSrc once we first receive a URL from the parent. After
  // that, swaps happen only in `handleError` below.
  useEffect(() => {
    if (!currentSrc && src) setCurrentSrc(src);
  }, [src, currentSrc]);

  const handleLoad = useCallback(() => {
    setLoaded(true);
    onLoaded?.();
  }, [onLoaded]);

  const isStale = useCallback(() => {
    if (!capturedAt) return false;
    const t = new Date(capturedAt).getTime();
    if (Number.isNaN(t)) return false;
    return Date.now() - t > STALE_AFTER_MS;
  }, [capturedAt]);

  // Parent already knows the capture failed (file_size = -1) — render
  // an explicit failed state in error-red, distinct from the generic
  // "Image unavailable" we use for pending-then-stale rows. Operator
  // can tell apart "device declined" from "slow upload that never
  // finished".
  if (forceFailed) {
    return (
      <Typography variant="caption" sx={{ color: '#E60A32', fontWeight: 500 }}>
        Capture failed
      </Typography>
    );
  }

  // Surface retry attempts so the operator knows the image is
  // re-fetching rather than just hanging on "Loading…".
  const retrying = attemptsRef.current > 0 && attemptsRef.current < maxAttempts;
  const retryExhausted = !!currentSrc && attemptsRef.current >= maxAttempts && !loaded;

  if (!currentSrc) {
    // No signed URL yet — if the row itself is already old, the device
    // clearly never produced an image; otherwise wait.
    if (isStale()) {
      return (
        <Typography variant="caption" color="text.secondary">
          Image unavailable
        </Typography>
      );
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        <CircularProgress size={20} />
        <Typography variant="caption" color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  if (failed) {
    return (
      <Typography variant="caption" color="text.secondary">
        Image unavailable
      </Typography>
    );
  }

  if (retryExhausted) {
    // The DEVICE upload succeeded (we only got here with a non-empty
    // currentSrc), the browser just couldn't fetch the thumbnail —
    // most likely the signed URL expired (1h TTL) or there was a
    // browser→GCS network blip. Clicking resets local retries so the
    // next render re-attempts; a fresh page load (or the gallery's
    // 5s polling tick when pending rows exist) mints a new signed
    // URL via the list endpoint.
    return (
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          attemptsRef.current = 0;
          setBust((b) => b + 1);
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: '#6B7280',
          p: 1,
          '&:hover': { color: '#111' },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          Couldn't load image
        </Typography>
        <Typography variant="caption" sx={{ fontSize: 11, color: '#9CA3AF' }}>
          Click to retry
        </Typography>
      </Box>
    );
  }

  const busted = bust > 0 ? `${currentSrc}${currentSrc.includes('?') ? '&' : '?'}_r=${bust}` : currentSrc;

  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height }}>
      <Box
        component="img"
        src={busted}
        alt=""
        decoding="async"
        onLoad={handleLoad}
        onError={() => {
          setLoaded(false);
          if (isStale()) {
            setFailed(true);
            return;
          }
          if (attemptsRef.current >= maxAttempts) {
            // Budget exhausted on this URL. Adopt whatever the parent
            // is currently offering (poll cycles may have handed us a
            // fresher signature or a different object that has since
            // landed) and reset the retry counter so the new URL gets
            // its own shot.
            if (src && src !== currentSrc) {
              attemptsRef.current = 0;
              setBust(0);
              setCurrentSrc(src);
            }
            // Otherwise leave the UI on "Loading…" — the src-sync
            // effect will kick us back into action as soon as the
            // parent provides a new URL.
            return;
          }
          attemptsRef.current += 1;
          timerRef.current = setTimeout(() => {
            setBust((b) => b + 1);
          }, retryDelayMs);
        }}
        sx={{
          display: loaded ? 'block' : 'none',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
        }}
      />
      {!loaded && (
        <Box
          aria-label={alt}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            bgcolor: '#f5f5f5',
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="caption" color="text.secondary">
            {retrying ? 'Retrying…' : 'Loading…'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

interface ScreenshotGalleryProps {
  screenshots: Screenshot[];
  total: number;
  isLoading: boolean;
  filter: ScreenshotFilter;
  onFilterChange: (filter: ScreenshotFilter) => void;
  onView: (screenshot: Screenshot) => void;
  onDelete: (id: string) => void;
  isDeleting?: boolean;
}

export function ScreenshotGallery({
  screenshots,
  total,
  isLoading,
  filter,
  onFilterChange,
  onView,
  onDelete,
}: ScreenshotGalleryProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // markLoaded was used to opt loaded-but-unverified thumbnails into
  // the "can view" set. Detail panel is now openable for every row
  // (see canView change below) so we keep the prop for ScreenshotThumb
  // backwards-compat but no longer track the ids.
  const markLoaded = useCallback((_id: string) => {}, []);

  const rowsPerPage = filter.limit || PAGE_SIZE;
  const zeroBasedPage = Math.floor((filter.offset || 0) / rowsPerPage);

  const handlePageChange = (_: unknown, page: number) => {
    onFilterChange({ ...filter, offset: page * rowsPerPage });
  };

  const handleRowsPerPageChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newLimit = parseInt(e.target.value, 10);
    onFilterChange({ ...filter, limit: newLimit, offset: 0 });
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }} />
        {/* Compare mode removed in the redesign — the dead "Compare
            selected" IconButton used to render here. Selection state +
            "N selected" chip stay so multi-select can be reused if
            another bulk action is added later. */}
        {selected.size > 0 && (
          <Chip
            label={`${selected.size} selected`}
            onDelete={() => setSelected(new Set())}
            size="small"
          />
        )}
      </Box>

      {/* Grid */}
      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={200} />
            </Grid>
          ))}
        </Grid>
      ) : screenshots.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No captures yet. Click "Capture now" to take the first one.
        </Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            {screenshots.map((screenshot) => {
              const isSelected = selected.has(screenshot.id);
              // Open the detail panel for every row — the panel now
              // renders the lifecycle timeline + metadata even when no
              // image is available, which is useful for diagnosing
              // loading rows (file_size = 0, awaiting GCS HEAD) and
              // failed rows (file_size = -1, device-reported failure).
              // Delete is always allowed.
              const canView = true;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={screenshot.id}>
                  <Card
                    sx={{
                      position: 'relative',
                      cursor: canView ? 'pointer' : 'default',
                      border: isSelected ? '2px solid' : '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      transition: 'border-color 0.2s',
                      '&:hover': { borderColor: isSelected ? 'primary.main' : 'action.hover' },
                      '&:hover .screenshot-actions': { opacity: 1 },
                    }}
                  >
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 1,
                          color: 'primary.main',
                          fontSize: 20,
                        }}
                      />
                    )}

                    {/* Image */}
                    <Box
                      onClick={canView ? () => onView(screenshot) : undefined}
                      sx={{
                        height: 180,
                        bgcolor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        cursor: canView ? 'pointer' : 'default',
                      }}
                    >
                      <ScreenshotThumb
                        // Withhold the signed URL until the row is
                        // verified-uploaded (file_size > 0). The
                        // backend signs URLs for every row including
                        // file_size === 0 ones, but GCS 404s those
                        // until the device's PUT completes — without
                        // this guard the thumb burns its retry budget
                        // on a 404 within ~9s of capture and then
                        // shows "Couldn't load image" forever (even
                        // though the next list refetch would have
                        // landed file_size > 0 and a working URL).
                        // Passing undefined leaves the thumb in its
                        // "Loading…" state, which the list's pending-
                        // row polling refreshes every 5s.
                        src={screenshot.file_size > 0 ? screenshot.download_url : undefined}
                        alt={`Screenshot ${screenshot.captured_at}`}
                        height={180}
                        capturedAt={screenshot.captured_at}
                        failed={screenshot.file_size < 0}
                        onLoaded={() => markLoaded(screenshot.id)}
                      />
                    </Box>

                    {/* Footer */}
                    <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                        {(() => {
                          // Tile label always shows captured_at (the
                          // capture request time) prefixed with the
                          // "Captured at:" label, matching the detail
                          // panel's metadata row for consistency. If
                          // uploaded_at diverges from captured_at by
                          // more than 30s — a real "late upload" — a
                          // tooltip surfaces the actual upload landing
                          // time so support can correlate.
                          const lateBy =
                            screenshot.uploaded_at
                              ? new Date(screenshot.uploaded_at).getTime() -
                                new Date(screenshot.captured_at).getTime()
                              : 0;
                          const showLateHint = lateBy > 30_000;
                          const formatted = (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography
                                component="span"
                                sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}
                              >
                                Sent command at:
                              </Typography>
                              <FormattedTime
                                value={screenshot.captured_at}
                                variant="absolute"
                                timeOptions={{
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  second: '2-digit',
                                }}
                                sx={{ fontSize: 12, color: 'text.secondary' }}
                              />
                            </Box>
                          );
                          return showLateHint ? (
                            <Tooltip
                              title={`Object landed in GCS ${Math.round(lateBy / 1000)}s after the capture request (${new Date(screenshot.uploaded_at!).toLocaleString()})`}
                              arrow
                            >
                              <span>{formatted}</span>
                            </Tooltip>
                          ) : (
                            formatted
                          );
                        })()}
                        <Box
                          className="screenshot-actions"
                          sx={{ opacity: 0, transition: 'opacity 0.2s', display: 'flex', gap: 0.5 }}
                        >
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(screenshot.id);
                              }}
                              // Don't disable on isDeleting — the trash icon
                              // only opens the confirm popup; it doesn't
                              // fire the mutation. Disabling it blocked QC
                              // from queueing deletes on other rows while
                              // a previous optimistic delete was still
                              // reconciling with the server.
                              sx={{ color: colors.primary.main }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Footer — the pager's own "Showing X-Y of N" already carries
              the total, so we don't duplicate it on the left. */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <TablePagination
              component="div"
              count={total}
              page={zeroBasedPage}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[8, 16, 32]}
              labelDisplayedRows={({ from, to, count }) =>
                `Showing ${from}-${to} of ${count.toLocaleString()} screenshot${count !== 1 ? 's' : ''}`
              }
              sx={{ borderTop: 0 }}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
